import React from 'react';
import styled, { css } from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { AUDIENCES } from 'helpers/enums';
import { UserContext, useNavigateWithContext } from 'state';
import PersonalInformation from 'pages/showing/components/personal-info';
import ExistingShowing from 'pages/showing/components/existing-showing';
import { useContextId, useOrgId } from 'hooks/useSessionVariables';
import { SAVE_APPLICATION_ITEM } from 'dal/application-items';
import { useAPIData } from 'dal/useAPIData';
import PropertyContentLayout from '../../layout/property-content';
import LocationList from '../../locations-list';
import LocationNotAvailable from 'components/location-not-available';

const Wrapper = styled.div`
  ${({ isMobile }) => {
    isMobile
      ? css`
          .locationsGrid {
            padding: 0;
            margin: 0;
          }
        `
      : css`
          .locationsGrid {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
          }
          .gridLocationItem {
            max-width: 400px;
            width: 400px;
          }
        `;
  }}
`;

export default ({ locationId: initialLocationId }) => {
  const isMobile = useMediaQuery({ maxWidth: 960 });
  const orgId = useOrgId();
  const contextId = useContextId();
  const navigate = useNavigateWithContext();
  const { userContext } = React.useContext(UserContext);
  const [searchParams] = useSearchParams();
  const fromShowingId = searchParams.get('fromShowingId') || undefined;
  // When rebooking after a unit became unavailable, clear the pre-selected unit
  // so the prospect sees the full list of available homes to choose from.
  const [locationId, setLocationId] = React.useState(
    fromShowingId ? null : initialLocationId || contextId
  );
  const [showLocationList, setShowLocation] = React.useState(fromShowingId ? true : !locationId);

  const [fromShowingData] = useAPIData(
    'applicationItems.GET_APPLICATION_ITEM',
    fromShowingId ? { userContext, objectId: fromShowingId } : undefined,
    { disableEmptyArgs: true, reFetchWhen: [fromShowingId || ''] }
  );
  const fromShowingApplicant = fromShowingData?.item?.applicant;
  const [saveInProgress, setSaveInProgress] = React.useState(false);
  const [existingShowing, setExistingShowing] = React.useState(false);
  const [warningModalOpen, setWarningModalOpen] = React.useState();

  // Proactively detect if the selected unit is no longer available for touring.
  // This handles the case where a user navigates directly to a locked unit's URL.
  const [locationData] = useAPIData(
    'context.LOAD_CONTEXT',
    locationId ? { contextId: locationId, audience: AUDIENCES.showing.applicationType } : undefined,
    { reFetchWhen: [locationId || ''], disableEmptyArgs: true }
  );
  React.useEffect(() => {
    if (locationData?.isShowingLocked) {
      setLocationId(null);
      setShowLocation(true);
      setWarningModalOpen(true);
    }
  }, [locationData?.isShowingLocked]);

  const handleContinue = (values) => {
    if (saveInProgress) {
      return;
    }

    setSaveInProgress(true);
    SAVE_APPLICATION_ITEM({
      userContext,
      data: {
        step: 'personalInfo',
        content: {
          locationId: locationId,
          companyId: orgId,
          applicationType: AUDIENCES.showing.applicationType,
          // When rebooking after a unit became unavailable, carry the source showing
          // ID so the backend can reuse existing verification and qualification data.
          ...(fromShowingId ? { fromShowingId } : {}),
          ...values
        }
      }
    })
      .then((showing) => {
        setSaveInProgress(false);
        if (showing?.item?._id && showing?.state === 'pre-existing') {
          setExistingShowing(showing.item);
        } else {
          navigate(`app/showing/${showing?.item?._id}/summary`);
        }
      })
      .catch((err) => {
        setSaveInProgress(false);
        if (err.data?.code === 'INACTIVE_LINK') {
          setWarningModalOpen(true);
          return setShowLocation(true);
        }
        console.error({ errData: err.data, err });
        toast.error(err.data?.message ? err.data?.message : err.message || err.name);
      });
  };

  return (
    <PropertyContentLayout
      locationId={locationId}
      hideWhenNoLocation={true}
      onChangeProperty={() => setShowLocation(true)}
      maxWidth={'100%'}>
      <Wrapper isMobile={isMobile} className="wrapper">
        {warningModalOpen && (
          <LocationNotAvailable
            open={warningModalOpen}
            onClose={() => setWarningModalOpen(false)}
          />
        )}
        {showLocationList ? (
          <LocationList
            defaultLocationId={locationData?._id}
            hideDefaultLocation={!!fromShowingId}
            selectContent={'Request showing'}
            onBack={locationId ? () => setShowLocation(false) : undefined}
            onSelect={(location) => {
              setLocationId(location._id);
              setShowLocation(false);
            }}
          />
        ) : existingShowing ? (
          <ExistingShowing
            showing={existingShowing}
            onShowView={() => navigate(`app/showing/${existingShowing._id}/summary`)}
            onChangeProperty={() => {
              setShowLocation(true);
              setExistingShowing(null);
            }}
          />
        ) : (
          <PersonalInformation
            values={fromShowingApplicant}
            location={locationData}
            onSubmit={handleContinue}
          />
        )}
      </Wrapper>
    </PropertyContentLayout>
  );
};
